import { GoogleOAuthProvider } from "@react-oauth/google";
import { ConfigProvider } from "antd";
import "antd/dist/reset.css";
import { ErrorBoundary } from "components/custom";
import themeV5 from "configs/themeV5";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import duration from "dayjs/plugin/duration";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import localeData from "dayjs/plugin/localeData";
import localizedFormat from "dayjs/plugin/localizedFormat";
import minMax from "dayjs/plugin/minMax";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import weekday from "dayjs/plugin/weekday";
import weekOfYear from "dayjs/plugin/weekOfYear";
import weekYear from "dayjs/plugin/weekYear";
import { getAppLanguage } from "helpers/getAppLanguage";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { inDevelopMode } from "utils/env";
import "./App.less";
import { configs } from "./configs";
import AppRouter from "./core/AppRouter";
import { AppModeProvider } from "./core/AppModeContext";
import "./i18n/i18n";
import { setAppDirection, setAppLocale } from "./store/appSlice";

dayjs.extend(advancedFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
dayjs.extend(utc);
dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(quarterOfYear);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
dayjs.extend(localizedFormat);
dayjs.extend(timezone);
dayjs.extend(minMax);

const AppContent = () => {
  const dispatch = useDispatch();
  const { direction } = useSelector((state) => state.app.configs);
  const language = getAppLanguage();

  useEffect(() => {
    const currentPath = window.location.pathname;
    const pathDirectories = currentPath.split("/").filter(Boolean);
    const firstPathSegment = pathDirectories[0];
    
    const excludedPaths = ["admin"];
    if (excludedPaths.includes(firstPathSegment)) {
      return;
    }
    const isValidLanguage = configs.languages.some(lang => lang.key === firstPathSegment);
    const defaultLang = configs.languages[0].key;
    if (!firstPathSegment) {
      window.location.replace(`/${defaultLang}${currentPath === "/" ? "" : currentPath}`);
      return;
    }
    if (!isValidLanguage) {
      const pathWithoutLang = currentPath.replace(/^\/[^/]+/, "");
      window.location.replace(`/${defaultLang}${pathWithoutLang || "/fms/dashboard"}`);
      return;
    }
  }, []);

  useEffect(() => {
    if (language?.key) {
      dispatch(setAppLocale(language.locale));
      dispatch(setAppDirection(language?.rtl ? "rtl" : "ltr"));
    }
  }, [language]);

  const renderPFRoutes = useMemo(() => {
    return inDevelopMode ? (
      <AppRouter />
    ) : (
      <ErrorBoundary>
        <AppRouter />
      </ErrorBoundary>
    );
  }, []);

  useEffect(() => {
    if (language) {
      const htmlElement = document.getElementsByTagName("html")[0];
      htmlElement.lang = language.locale;
      htmlElement.dir = language.rtl ? "rtl" : "ltr";
      const fontLink = document.getElementById("link-app-fonts");
      if (fontLink) {
        fontLink.href = language.font;
      }
    }
  }, [language]);

  // Support both env var names for compatibility
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || process.env.REACT_APP_GOOGLE_AUTH_CLIENT_ID;

  return (
    <ConfigProvider
      locale={language?.locale}
      direction={direction}
      theme={themeV5}
    >
      <GoogleOAuthProvider clientId={googleClientId || ""}>
        <BrowserRouter>
          <AppModeProvider>{renderPFRoutes}</AppModeProvider>
        </BrowserRouter>
      </GoogleOAuthProvider>
    </ConfigProvider>
  );
};

function App() {
  return <AppContent />;
}

export default App;